/**
 * Y-axis tick generation, pulled out of LineChart as pure logic.
 *
 * "Nice" round numbers (0 / 1,000 / 2,000, never 0 / 743 / 1486) are what
 * make an axis readable at a glance instead of requiring the reader to do
 * arithmetic.
 */
export function niceTicks(min: number, max: number, count = 5): number[] {
  if (min === max) return [min];
  const span = max - min;
  const rawStep = span / count;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const normalized = rawStep / magnitude;
  const step =
    (normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10) * magnitude;

  const niceMin = Math.floor(min / step) * step;
  const niceMax = Math.ceil(max / step) * step;
  const ticks: number[] = [];
  for (let v = niceMin; v <= niceMax + step / 2; v += step) ticks.push(Number(v.toFixed(10)));
  return ticks;
}

export function formatTick(value: number): string {
  if (Math.abs(value) >= 1000) return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
  return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

/**
 * Advance width of one character, as a fraction of font size.
 *
 * The whole interface is monospace, which is what makes measuring a label by
 * counting its characters legitimate here rather than a guess. Erring high by
 * a little costs a few pixels of plot width; erring low clips the label, so
 * this sits at the wide end of the range real monospace faces use.
 */
const CHAR_WIDTH_RATIO = 0.62;

/** Gap between the longest tick label and the axis it labels. */
const LABEL_GAP = 8;

/**
 * Left margin wide enough for the widest tick label.
 *
 * A fixed margin works until it does not: at 56px it fit "0" and "500,000"
 * and silently cut the leading digit off "2,000,000", so an interface
 * carrying megabits displayed an axis reading ",000,000". The margin has to
 * follow the labels, because the labels are what it exists to make room for.
 *
 * The floor is that same 56px rather than something tighter, so charts stacked
 * in a column keep sharing a left edge. Only an axis that genuinely needs more
 * room takes more, which means nothing that renders correctly today moves.
 */
export function axisMargin(labels: string[], fontSize = 11, minimum = 56): number {
  const longest = labels.reduce((max, label) => Math.max(max, label.length), 0);
  return Math.max(minimum, Math.ceil(longest * fontSize * CHAR_WIDTH_RATIO) + LABEL_GAP);
}
